import { AApi } from './AApi';
import type { ApiResult, PaginationParams } from '../types/api.types';
import type { RecordRights, RecordTag } from '../types/entities.types';
import type { RecordAccessBody } from '../types/rights.types';
import type {
    IntranetDetails,
    IntranetEntryBody,
    IntranetEntryInput,
    IntranetListBody,
    IntranetPointBody,
    IntranetPointDetails,
    IntranetPointInput,
} from '../types/intranet.types';

/** The intranet module: `/intranets`, `/intranetPoints`, `/intranetEntries`. */
export class IntranetApi extends AApi {
    public createEntry(pointId: number, data: IntranetEntryInput): Promise<ApiResult<{ entry: IntranetEntryBody }>> {
        return this.Client.post(`${this.points(pointId)}/entries`, data);
    }

    public createIntranet(intranet: string): Promise<ApiResult<IntranetDetails>> {
        return this.Client.post(this.intranets(), { intranet });
    }

    public createPoint(intranetId: number, data: IntranetPointInput): Promise<ApiResult<{ point: IntranetPointBody }>> {
        return this.Client.post(`${this.intranets(intranetId)}/points`, data);
    }

    public deleteEntry(entryId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.entries(entryId));
    }

    public deleteIntranet(intranetId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.intranets(intranetId));
    }

    public deletePoint(pointId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.points(pointId));
    }

    public getIntranet(intranetId: number): Promise<ApiResult<IntranetDetails>> {
        return this.Client.get(this.intranets(intranetId));
    }

    public getPoint(pointId: number): Promise<ApiResult<IntranetPointDetails>> {
        return this.Client.get(this.points(pointId));
    }

    /** For admins: who reaches the intranet on which level. */
    public getRecordAccess(intranetId: number): Promise<ApiResult<RecordAccessBody>> {
        return this.Client.get(`${this.intranets(intranetId)}/rights`);
    }

    public listIntranets(params?: PaginationParams): Promise<ApiResult<IntranetListBody>> {
        return this.Client.get(`${this.intranets()}${this.toQuery(params)}`);
    }

    public replaceTags(intranetId: number, tags: string[]): Promise<ApiResult<{ tags: RecordTag[]; rights: RecordRights }>> {
        return this.Client.put(`${this.intranets(intranetId)}/tags`, { tags });
    }

    public sortEntries(pointId: number, order: number[]): Promise<ApiResult<{ entries: IntranetEntryBody[] }>> {
        return this.Client.put(`${this.points(pointId)}/entries/sort`, { order });
    }

    public sortPoints(intranetId: number, parentId: number | null, order: number[]): Promise<ApiResult<IntranetDetails>> {
        return this.Client.put(`${this.intranets(intranetId)}/points/sort`, { parent_id: parentId, order });
    }

    public updateEntry(entryId: number, data: IntranetEntryInput): Promise<ApiResult<{ entry: IntranetEntryBody }>> {
        return this.Client.patch(this.entries(entryId), data);
    }

    public updateIntranet(intranetId: number, intranet: string): Promise<ApiResult<IntranetDetails>> {
        return this.Client.patch(this.intranets(intranetId), { intranet });
    }

    public updatePoint(pointId: number, data: IntranetPointInput): Promise<ApiResult<{ point: IntranetPointBody }>> {
        return this.Client.patch(this.points(pointId), data);
    }

    private entries(entryId: number): string {
        return `${this.VersionPath}/intranetEntries/${entryId.toString()}`;
    }

    private intranets(intranetId?: number): string {
        return `${this.VersionPath}/intranets${intranetId === undefined ? '' : `/${intranetId.toString()}`}`;
    }

    private points(pointId: number): string {
        return `${this.VersionPath}/intranetPoints/${pointId.toString()}`;
    }
}
